/**
 * Locale Storage Utilities
 * Handles persistence of locale preference in localStorage
 */

// Constants
export const LOCALE_STORAGE_KEY = 'preferred_locale';
export const SUPPORTED_LOCALES = ['en', 'es'] as const;
export type SupportedLocale = typeof SUPPORTED_LOCALES[number];
export const DEFAULT_LOCALE: SupportedLocale = 'en';

/**
 * Check if a locale string is a valid supported locale
 */
export function isValidLocale(locale: string): locale is SupportedLocale {
  return SUPPORTED_LOCALES.includes(locale as SupportedLocale);
}

/**
 * Get stored locale from localStorage
 * Returns null if not found or invalid
 */
export function getStoredLocale(): SupportedLocale | null {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored && isValidLocale(stored)) {
      return stored;
    }
    return null;
  } catch {
    // localStorage might not be available (private browsing, etc.)
    // eslint-disable-next-line no-console
    console.warn('Unable to access localStorage for locale preference');
    return null;
  }
}

/**
 * Store locale preference in localStorage
 */
export function setStoredLocale(locale: SupportedLocale): void {
  try {
    if (isValidLocale(locale)) {
      localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    }
  } catch {
    // localStorage might not be available (private browsing, etc.)
    // eslint-disable-next-line no-console
    console.warn('Unable to save locale preference to localStorage');
  }
}

/**
 * Clear stored locale preference from localStorage
 */
export function clearStoredLocale(): void {
  try {
    localStorage.removeItem(LOCALE_STORAGE_KEY);
  } catch {
    // localStorage might not be available (private browsing, etc.)
    // eslint-disable-next-line no-console
    console.warn('Unable to clear locale preference from localStorage');
  }
}

// Re-export detection utilities for convenience
export {
  detectBrowserLocale,
  detectLocaleWithRegion,
  getFormattingLocale,
  getDetectedRegion,
  isUSRegion,
} from './detectLocale';

// Import for getInitialLocale
import { detectBrowserLocale } from './detectLocale';

/**
 * Get initial locale based on priority:
 * 1. localStorage (user's saved preference)
 * 2. Browser language detection
 * 3. Default locale (English)
 * 
 * Note: User preference from API is handled separately in useLocale/IntlProvider
 */
export function getInitialLocale(): SupportedLocale {
  // Priority 1: Check localStorage
  const stored = getStoredLocale();
  if (stored) {
    return stored;
  }
  
  // Priority 2: Detect from browser
  return detectBrowserLocale();
}
